<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRoute } from 'vue-router'
import { BODY_MOTION_CLASS } from '@/lib/navigation/direction'
import { navigationMotion } from '@/router/navigation'
import { SECTIONS, type SectionId } from '@/router/sections'
import AppPage from '../AppPage/AppPage.vue'
import { provideSectionHeader } from './section-header'

const props = defineProps<{
  /** Which tabbed section this layout renders; picks the title, tab bar and whether the body owns its scrolling. */
  section: SectionId
}>()

const { t } = useI18n()
const route = useRoute()
const header = provideSectionHeader()

const config = computed(() => SECTIONS[props.section])
const description = computed(() => (route.meta.description ? t(route.meta.description) : undefined))
const breadcrumbs = computed(() => [{ id: props.section, label: t(config.value.titleKey) }])
const tabs = computed(() => config.value.tabs(t))

const ActionsOutlet = (): ReturnType<NonNullable<typeof header.actions.value>> | undefined =>
  header.actions.value?.()

const PinnedOutlet = (): ReturnType<NonNullable<typeof header.pinned.value>> | undefined =>
  header.pinned.value?.()
</script>

<template>
  <AppPage
    :breadcrumbs="breadcrumbs"
    :description="description"
    :tabs="tabs"
    :tabs-aria-label="t(config.tabsAriaKey)"
    :full-height="config.fullHeight"
    :scroll-tail="header.scrollTail.value"
  >
    <template v-if="header.actions.value" #actions>
      <ActionsOutlet />
    </template>
    <template #pinned>
      <PinnedOutlet />
    </template>
    <RouterView v-slot="{ Component, route: active }">
      <div
        :key="String(active.name)"
        class="flex min-h-0 flex-1 flex-col"
        :class="BODY_MOTION_CLASS[navigationMotion]"
      >
        <component :is="Component" />
      </div>
    </RouterView>
  </AppPage>
</template>
